import React from 'react';
import styled from 'styled-components';
import { Button, Icon } from '@repo/design-system';
import { space } from '@repo/design-tokens';

/**
 * **Twin details on a twin that already exists** (`twin-details-page-summary`, R1–R2; frame 03):
 * four read-first cards — Identity, Address, Timezone, and Position and opening view — each with its
 * own *Edit*, in place of the always-open form a new twin gets. Every change still saves straight to
 * the twin through the page's queue, field by field.
 *
 * The cards are passed in already wired: the step owns the read, the saved state and the saves, and
 * this owns only the arrangement. At 1280 px and wider, Identity, Address and Timezone sit in one row
 * and Position spans it below; narrower, they stack in that order (R2.1). *Next: Opening Hours*
 * stays below, so the way on is the same for every twin (R2.5; undrawn, Human Action 1).
 */

interface Props {
  className?: string;
  identity: React.ReactNode;
  address: React.ReactNode;
  timezone: React.ReactNode;
  position: React.ReactNode;
  onNext: () => void;
}

const TwinDetailsSummary: React.FC<Props> = ({
  className,
  identity,
  address,
  timezone,
  position,
  onNext,
}) => (
  <div className={className}>
    <div className="cards">
      {identity}
      {address}
      {timezone}
      <div className="wide">{position}</div>
    </div>
    <div className="next">
      <Button variant="primary" iconRight={<Icon name="arrow-right" />} onClick={onNext}>
        Next: Opening Hours
      </Button>
    </div>
  </div>
);

export default styled(TwinDetailsSummary)`
  display: flex;
  flex-direction: column;
  gap: ${space[4]};

  .cards {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: ${space[4]};
    align-items: stretch;
  }

  .wide {
    display: flex;
    flex-direction: column;
  }

  .wide > * {
    flex: 1;
  }

  @media (min-width: 1280px) {
    .cards {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .wide {
      grid-column: 1 / -1;
    }
  }

  .next {
    display: flex;
    justify-content: flex-end;
  }
`;
